import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mantine/dates";
import { Popover } from "@mantine/core";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import PlaceIcon from "@mui/icons-material/Place";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box } from '@mui/material';
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import base_url from "../../api/bootapi";
import { useExperienceCart } from "./ExperienceCartContext";
import ExperienceProductCard from "./ExperienceProductCard";
import { CalendarMonth, Close, CropSquareSharp, PlaceOutlined } from "@mui/icons-material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";



import {
  cacheProduct,
  getRating,
  getFromPrice,
  formatDate,
  formatDateRange,
  getTop10RootTags,
} from "./experienceUtils";
import "./Experiences.css";
import debounce from "lodash.debounce";
import FilterModal from "./FilterModal";

const DURATION_OPTIONS = [
  { key: "under2", label: "Up to 2 hours", test: (mins) => mins != null && mins <= 120 },
  { key: "2to4", label: "2 - 4 hours", test: (mins) => mins != null && mins > 120 && mins <= 240 },
  { key: "4to24", label: "4 hours - 1 day", test: (mins) => mins != null && mins > 240 && mins <= 1440 },
  { key: "multi", label: "Multi-day", test: (mins) => mins != null && mins > 1440 },
];

const PAGE_SIZE = 24;

// Builds a compact page list like [1, 2, 3, '...', 263] - always keeps the
// first page, the last page, and a small window around the current page,
// collapsing everything else into a single '...' gap.
function buildPageList(current, total) {
  if (total <= 1) return [1];
  const pages = new Set([1, total, current]);
  if (current - 1 > 1) pages.add(current - 1);
  if (current + 1 < total) pages.add(current + 1);

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((page, idx) => {
    if (idx > 0 && page - sorted[idx - 1] > 1) result.push("...");
    result.push(page);
  });
  return result;
}

const RATING_OPTIONS = [4, 3];

// Quick category chips shown under the filter bar. Since the destinations
// API doesn't expose Viator's tag taxonomy to us, these filter by matching
// the chip's keywords against each product's title/description — a
// lightweight client-side approximation of the reference design's category
// filters rather than a true tag-based filter.
const CATEGORY_CHIPS = [
  { key: "tours", label: "Tours, Sightseeing & Cruises", keywords: ["tour", "sightseeing", "cruise", "boat"] },
  { key: "outdoor", label: "Outdoor Activities", keywords: ["outdoor", "hike", "safari", "adventure", "desert"] },
  { key: "luxury", label: "Private and Luxury", keywords: ["private", "luxury", "vip"] },
  { key: "sightseeing", label: "Sightseeing", keywords: ["sightseeing", "landmark", "monument"] },
  { key: "food", label: "Food & Drink", keywords: ["food", "dining", "wine", "tasting", "culinary"] },
  { key: "water", label: "Water Sports", keywords: ["diving", "snorkel", "kayak", "water", "beach"] },
];

export default function ExperienceResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count: cartCount } = useExperienceCart();

  const params = new URLSearchParams(location.search);
  const destinationFromState = location.state && location.state.destination;

  const destination =
     {
      destination_id: params.get("destination_id"),
      name: params.get("name") || "your destination",
      default_currency_code: params.get("currency") || "USD",
    } || destinationFromState ;

  const [destinationQuery, setDestinationQuery] = useState(destination.name || "");
  const [products, setProducts] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [sortBy, setSortBy] = useState("DEFAULT");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [durationFilters, setDurationFilters] = useState([]);
  const [ratingFilter, setRatingFilter] = useState(null);
  const [freeCancelOnly, setFreeCancelOnly] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTag, setActiveTag] = useState([]);
  const [tags, setTags] = useState([])
  
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Function to open the modal
  const handleOpenFilter = () => {
    setIsFilterOpen(true);
  };

  // Function to close the modal
  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const handleApplyFilters = (filterPayload) => {
    console.log("Sending to API: ", filterPayload);
    // Call your API or update your list state here
    setFilters(filterPayload)
  };


  const tagsScrollRef = useRef(null);

const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const checkTagScroll = () => {
  const el = tagsScrollRef.current;

  if (!el) return;

  setCanScrollLeft(el.scrollLeft > 0);

  setCanScrollRight(
    el.scrollLeft + el.clientWidth < el.scrollWidth - 1
  );
  };
  
  useEffect(() => {
  const el = tagsScrollRef.current;

  if (!el) return;

  checkTagScroll();

  el.addEventListener("scroll", checkTagScroll);
  window.addEventListener("resize", checkTagScroll);

  return () => {
    el.removeEventListener("scroll", checkTagScroll);
    window.removeEventListener("resize", checkTagScroll);
  };
}, [tags]);

  // Filter bar chip state
  const initialStart = params.get("start");
  const initialEnd = params.get("end");
  const [dateRange, setDateRange] = useState([
    initialStart ? new Date(initialStart) : null,
    initialEnd ? new Date(initialEnd) : null,
  ]);
  const [showDatePopover, setShowDatePopover] = useState(false);
  const [adults, setAdults] = useState(2);
  const [showAdultsPopover, setShowAdultsPopover] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const [specials, setSpecials] = useState([])
  const [price, setPrice] = useState([null, null]);
  const [currencyType, setCurrencyType] = useState(null);
  const [durations,setDurations]=useState([])
  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(null, 1);
    fetchFilers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination.destination_id]);

  console.log(activeTag)
  useEffect(() => {
    
      fetchProducts(null, 1)
    
  }, [activeTag,sortBy])

  const [filters, setFilters] = useState(null);
  
  useEffect(() => {
    if (filters) {
      fetchProducts(null, 1)
    }
  }, [filters])

 

  
  


  const fetchProducts = async(overrideRange, page = 1) => {
    if (!destination.destination_id) {
      setLoading(false);
      setErrorMsg("No destination selected. Please start a new search.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    const range = overrideRange || dateRange;
    let filtering = { destination: String(destination.destination_id) };
    if (range[0] && range[1]) {
      filtering.startDate = formatDate(range[0]);
      filtering.endDate = formatDate(range[1]);
    }

    if (activeTag?.length > 0) {
      filtering.tags = activeTag
    }

    let languages =[]

    if (filters) {
      if (filters?.rating) {
        if (filters?.rating === 'FIVE_STAR') {
          filtering.rating = {to:5}
        }

         if (filters?.rating === 'FOUR_STAR') {
          filtering.rating = {from:4,to:5}
        }

        if (filters?.rating === 'THREE_STAR') {
          filtering.rating = {from:3,to:5}
        }
      }

      console.log(filters)

      if (filters?.specials?.length > 0) {
        filtering.flags = filters?.specials
      }

       if (filters?.minPrice > 0) {
        filtering.lowestPrice = filters?.minPrice
      }

        if (filters?.maxPrice > 0) {
        filtering.highestPrice = filters?.maxPrice
      }

      if (filters?.durations) {
        const durationObject = durations?.find((d) => d?.key === filters?.durations);
        filtering.durationInMinutes = {
          from: durationObject?.minMinutes,
          to:durationObject?.maxMinutes
        }
      }

      
      if (filters?.appliedLanguages) {
        languages = filters?.appliedLanguages?.join(', ');
      }


    }

    let sortingObject = { sort: "DEFAULT" }
    
    if (sortBy === 'PRICE_LOW') {
      sortingObject =  {
sort: "PRICE",
order: "ASCENDING"
}
    } else if (sortBy === 'PRICE_HIGH') {
      sortingObject = {
        sort: "PRICE",
        order: "DESCENDING"
      }
    } else {
      sortingObject =   { sort: "DEFAULT" }
    }
    

    const payload = {
      language: "en-US",
      filtering,
      sorting: sortingObject,
      pagination: { start: (page - 1) * PAGE_SIZE + 1, count: PAGE_SIZE },
      currency:  "USD",
    };

    const config = {
  headers: {
    ...(languages?.length > 1 && {
      "Accept-Language": languages,
    }),
  },
};

    try {
      const response = await axios.post(`${base_url.api1}/tourapp/viator/products/search`, payload,config);
      if (response.data && response.data.status) {
        const viatorResponse = response.data.viator_response || {};
        const list = viatorResponse.products || [];
        list.forEach(cacheProduct);
        setProducts(list);
        setTotalCount(viatorResponse.totalCount || list.length);
      } else {
        setProducts([]);
        setTotalCount(0);
        setErrorMsg("No experiences found for this search.");
      }
    } catch (error) {
      console.error("Error searching experiences:", error);
      setProducts([]);
      setTotalCount(0);
      setErrorMsg("Something went wrong while searching. Please try again.");
    } finally {
      setLoading(false);
    }
  };


   const fetchFilers = async () => {
    if (!destination.destination_id) {
      
      setErrorMsg("No destination selected. Please start a new search.");
      return;
    }
   
    setErrorMsg("");

   

    const payload = {
      language: "en",
      filtering: {
        "destination":destination.destination_id
      },
     
      currency: "USD",
    };

    try {
      const response = await axios.post(`${base_url.api1}/tourapp/viator/products/filters`, payload);
      if (response.data && response.data.status) {
        const viatorResponse = response.data || {};
        console.log(viatorResponse)

        // const top10Tags = getTop10RootTags(viatorResponse?.filters?.tags || [])

        const dur = viatorResponse?.filters?.durations || []
        const flags = viatorResponse?.filters?.flags || []
        const rangePrice = [viatorResponse?.filters?.price?.min,viatorResponse?.filters?.price?.max]
        const cur = viatorResponse?.filters?.price?.currency 
        setDurations(dur);
        setSpecials(flags);
        setPrice(rangePrice);
        setCurrencyType(cur);
        setTags(viatorResponse?.filters?.tags || [])
      } else {
       
        setErrorMsg("No experiences found for this search.");
      }
    } catch (error) {
     
      setErrorMsg("Something went wrong while searching. Please try again.");
    } 
  };
  const handlePageChange = (page) => {
    if (page === currentPage || page < 1) return;
    setCurrentPage(page);
    fetchProducts(null, page);
    const grid = document.querySelector(".exp-product-grid");
    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditSearch = (e) => {
    e.preventDefault();
    navigate("/experiences");
  };

  const toggleDuration = (key) => {
    setDurationFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setDurationFilters([]);
    setRatingFilter(null);
    setFreeCancelOnly(false);
  };

  // const filteredProducts = useMemo(() => {
  //   let list = [...products];

  //   if (freeCancelOnly) {
  //     list = list.filter((p) => p.flags && p.flags.includes("FREE_CANCELLATION"));
  //   }

  //   if (ratingFilter) {
  //     list = list.filter((p) => {
  //       const r = getRating(p);
  //       return r !== null && r >= ratingFilter;
  //     });
  //   }

  //   if (durationFilters.length) {
  //     list = list.filter((p) => {
  //       const mins = p.duration && p.duration.fixedDurationInMinutes;
  //       return durationFilters.some((key) => {
  //         const opt = DURATION_OPTIONS.find((o) => o.key === key);
  //         return opt && opt.test(mins);
  //       });
  //     });
  //   }

  //   if (activeCategory) {
  //     const chip = CATEGORY_CHIPS.find((c) => c.key === activeCategory);
  //     if (chip) {
  //       list = list.filter((p) => {
  //         const haystack = `${p.title || ""} ${p.description || ""}`.toLowerCase();
  //         return chip.keywords.some((kw) => haystack.includes(kw));
  //       });
  //     }
  //   }

  //   const min = parseFloat(minPrice);
  //   const max = parseFloat(maxPrice);
  //   if (!Number.isNaN(min)) {
  //     list = list.filter((p) => {
  //       const price = getFromPrice(p);
  //       return price !== null && price >= min;
  //     });
  //   }
  //   if (!Number.isNaN(max)) {
  //     list = list.filter((p) => {
  //       const price = getFromPrice(p);
  //       return price !== null && price <= max;
  //     });
  //   }

  //   if (sortBy === "PRICE_LOW") {
  //     list.sort((a, b) => (getFromPrice(a) ?? Infinity) - (getFromPrice(b) ?? Infinity));
  //   } else if (sortBy === "PRICE_HIGH") {
  //     list.sort((a, b) => (getFromPrice(b) ?? -Infinity) - (getFromPrice(a) ?? -Infinity));
  //   } else if (sortBy === "RATING") {
  //     list.sort((a, b) => (getRating(b) ?? 0) - (getRating(a) ?? 0));
  //   }

  //   return list;
  // }, [products, freeCancelOnly, ratingFilter, durationFilters, minPrice, maxPrice, sortBy, activeCategory]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleSelectProduct = (product) => {
    cacheProduct(product);
    navigate(`/experiences/details/${product.productCode}`, {
      state: { product, destination },
    });
  };


    const [destinationOptions, setDestinationOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [selectedDestination, setSelectedDestination] = useState(null);
    const [showDestDropdown, setShowDestDropdown] = useState(false);
    const [destLoading, setDestLoading] = useState(false);
  const [autoSearching, setAutoSearching] = useState(false);
    const latestQueryRef = useRef("");
  
  

  const fetchDestinationsRef = useRef(
      debounce(async (query, onDone) => {
        if (!query || query.trim().length < 2) {
          setDestinationOptions([]);
          setProductOptions([]);
          return;
        }
        latestQueryRef.current = query;
        setDestLoading(true);
        try {
          const response = await axios.get(
            `${base_url.api1}/tourapp/viator/destinations?search=${encodeURIComponent(
              query
            )}&page=1&count=10`
          );
          // Guard against out-of-order responses: if the user has since
          // typed something else, ignore this now-stale result.
          if (latestQueryRef.current !== query) return;
         const data = response.data;
        const destResults = data && data.status ? data.destinations || [] : [];
          const prodResults = data && data.status ? data.products || [] : [];
          
          const finalDestinations = destResults?.filter((dest) => dest.destination_id)
          console.log(finalDestinations)
  
        setDestinationOptions(finalDestinations);
          setProductOptions(prodResults); // Save products to state
          
          if (onDone) onDone({ destinations: destResults, products: prodResults });
        } catch (error) {
          if (latestQueryRef.current !== query) return;
          console.error("Error fetching destinations:", error);
         setProductOptions([]);
        if (onDone) onDone({ destinations: [], products: [] });
        } finally {
          if (latestQueryRef.current === query) setDestLoading(false);
        }
      }, 400)
    );
  const fetchDestinations = fetchDestinationsRef.current;
  
   const handleDestinationInputChange = (e) => {
    const value = e.target.value;
    setDestinationQuery(value);
    setSelectedDestination(null);
    setShowDestDropdown(true);
    fetchDestinations(value);
  };

  
const [isMoreModalOpen, setIsMoreModalOpen] = useState(false);
  
  const handleSelectDestination = (destination) => {
    console.log(destination)
    setSelectedDestination(destination);
    setDestinationQuery(destination.name);
    setShowDestDropdown(false);

     const params = new URLSearchParams(location.search);

  params.set("destination_id", destination.destination_id);
  params.set("name", destination.name);
  params.set("currency",  "USD");

  navigate(`${location.pathname}?${params.toString()}`);

  window.location.reload();

    // setShowMobileDestination(false);

  };

  const destWrapperRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (destWrapperRef.current && !destWrapperRef.current.contains(e.target)) {
          setShowDestDropdown(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

  return (
    <div className="exp-scope">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div style={{ position: "relative" }}>
        <Nav />
      </div>

     

      <div className="exp-container">
        <div className="exp-results-body">

          <div className="search-wrap" ref={destWrapperRef}>

               <div className="place-search"  >
            
            <SearchIcon fontSize="small" color="#222" />
            
             <input
                type="text"
                placeholder="Search for a place or activity"
                value={destinationQuery}
                onChange={handleDestinationInputChange}
               
               
                autoComplete="off"
              />
              

             
            
           
            

              </div>
                {showDestDropdown && destinationQuery.trim().length >= 2 && (
                <div className="exp-dest-dropdown" style={{minWidth:'750px'}}>
                  {destLoading && (
                    <div className="exp-dest-option exp-dest-loading">Searching...</div>
                  )}
                  {!destLoading && destinationOptions.length === 0 && (
                    <div className="exp-dest-option exp-dest-empty">
                      No matching places found
                    </div>
                  )}
                  {!destLoading &&
                    destinationOptions.map((dest, index) => {
                      // The row that matches what was typed reads as a real
                      // place (pin icon, highlighted) — every other partial
                      // match reads as a related suggestion (ticket icon),
                      // mirroring the reference design's mix of icon types.
                      const isExactMatch =
                        dest?.name?.trim().toLowerCase() ===
                        destinationQuery.trim().toLowerCase();
                      return (
                        <div
                          key={dest.destination_id || `${dest.name}-${index}`}
                          className={`exp-dest-option${
                            isExactMatch ? " exp-dest-option-highlight" : ""
                          }`}
                          onClick={() => handleSelectDestination(dest)}
                        >
                          {isExactMatch ? (
                            <PlaceOutlined fontSize="large" />
                          ) : (
                            <ConfirmationNumberOutlinedIcon fontSize="large" />
                          )}
                          <div>
                            <div className="exp-dest-name">{dest.name}</div>
                            {/* <div className="exp-dest-type">
                              {dest.parent_destination_name || dest.type}
                            </div> */}
                          </div>
                        </div>
                      );
                    })}
                
                {/* --- RENDER PRODUCTS --- */}
    {!destLoading && productOptions.length > 0 && (
      <div className="exp-dest-section-title" style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', color: '#666', backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}>
        Tours & Activities
      </div>
    )}
    {!destLoading &&
      productOptions.map((product, index) => {
        // Extract the smallest image variant for the dropdown thumbnail
        const coverImage = product.images?.[0]?.variants?.find(v => v.width === 75)?.url 
          || product.images?.[0]?.variants?.[0]?.url;

        return (
          <div
            key={product.productCode || `prod-${index}`}
            className="exp-dest-option"
            onClick={() => handleSelectProduct(product)} // Make sure to define this handler!
          >
            {coverImage ? (
              <img 
                src={coverImage} 
                alt={product.title} 
                style={{ width: 35, height: 35, borderRadius: 4, objectFit: 'cover', marginRight: 10 }} 
              />
            ) : (
              <ConfirmationNumberOutlinedIcon fontSize="large" style={{ marginRight: 10, color: "gray" }} />
            )}
            <div style={{ overflow: 'hidden' }}>
              <div className="exp-dest-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {product.title}
              </div>
              <div className="exp-dest-type">
                From ${product.pricing?.summary?.fromPrice} {product.pricing?.currency} • {product.reviews?.combinedAverageRating?.toFixed(1)} ★
              </div>
            </div>
          </div>
        );
      })}
                 
                </div>
              )}

             
              
              </div>
          <div className="exp-results-header">
            <div>
              <h1>
                {totalCount > 0
                  ? `${totalCount.toLocaleString()} experiences in ${destination.name}`
                  : `Experiences in ${destination.name}`}
              </h1>
              <p>Tours, tickets and activities you can book straight through Linsoa.</p>
            </div>
          
          </div>

          <div className="exp-filterbar">
            
            <div className="exp-filterbar-chips">
              <Popover
                opened={showDatePopover}
                onChange={setShowDatePopover}
                position="bottom-start"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <button
                    type="button"
                    className="exp-filter-chip"
                    onClick={() => setShowDatePopover((o) => !o)}
                  >
                    <EventAvailableIcon fontSize="small" />
                    {dateRange[0] && dateRange[1]
                      ? formatDateRange(dateRange[0], dateRange[1])
                      : "Any dates"}
                  </button>
                </Popover.Target>
                <Popover.Dropdown>
                  <DatePicker
                    type="range"
                    value={dateRange}
                    onChange={setDateRange}
                    minDate={new Date()}
                    allowSingleDateInRange
                  />
                  <button
                    type="button"
                    className="exp-popover-apply"
                    onClick={() => {
                      setShowDatePopover(false);
                      setCurrentPage(1);
                      fetchProducts(dateRange, 1);
                    }}
                  >
                    Apply dates
                  </button>
                </Popover.Dropdown>
              </Popover>

              <Popover
                opened={showAdultsPopover}
                onChange={setShowAdultsPopover}
                position="bottom-start"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <button
                    type="button"
                    className="exp-filter-chip"
                    onClick={() => setShowAdultsPopover((o) => !o)}
                  >
                    <PersonOutlineIcon fontSize="small" />
                    {adults} {adults === 1 ? "Adult" : "Adults"}
                    <ExpandMoreIcon fontSize="small" />
                  </button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div className="exp-adults-stepper">
                    <span>Adults</span>
                    <div className="exp-stepper-controls">
                      <button
                        type="button"
                        onClick={() => setAdults((a) => Math.max(1, a - 1))}
                        aria-label="Decrease adults"
                      >
                        <RemoveIcon fontSize="small" />
                      </button>
                      <span>{adults}</span>
                      <button
                        type="button"
                        onClick={() => setAdults((a) => a + 1)}
                        aria-label="Increase adults"
                      >
                        <AddIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                </Popover.Dropdown>
              </Popover>

              <button
                type="button"
                className="exp-filter-chip exp-filter-chip-solid"
                onClick={() => handleOpenFilter()}
              >
                <TuneIcon fontSize="small" /> Filters
              </button>
            </div>

            <span className="exp-filterbar-divider" />

           <div className="exp-category-wrapper">

  {canScrollLeft && (
    <button
      type="button"
      className="exp-category-arrow exp-category-arrow-left"
      onClick={() => {
         tagsScrollRef.current?.scrollTo({
    left: 0,
    behavior: "smooth",
  });
      }}
    >
      <ChevronLeftIcon />
    </button>
  )}

  <div
    ref={tagsScrollRef}
    className="exp-category-scroll"
  >
    {tags?.slice(0,10).map((tag) => {
      const isActive = activeTag.includes(tag.tagId);

      return (
        <button
          key={tag.tagId}
          type="button"
          className={`exp-category-pill ${
            isActive ? "exp-category-pill-active" : ""
          }`}
          onClick={() => {
            setActiveTag((prev) =>
              prev.includes(tag.tagId)
                ? prev.filter((id) => id !== tag.tagId)
                : [...prev, tag.tagId]
            );

            // setTags((prev) => {
            //   const selectedTag = prev.find(
            //     (item) => item.tagId === tag.tagId
            //   );

            //   return [
            //     selectedTag,
            //     ...prev.filter(
            //       (item) => item.tagId !== tag.tagId
            //     ),
            //   ];
            // });
          }}
        >
          {tag.name}

          {isActive && (
            <CloseIcon
              className="exp-category-close"
              fontSize="small"
            />
          )}
        </button>
      );
    })}
                
                {tags && tags.length > 0 && (
        <button
          type="button"
          className="exp-category-pill"
          onClick={() => setIsMoreModalOpen(true)}
          style={{ fontWeight: "bold" }}
        >
          More
        </button>
      )}
  </div>

  {canScrollRight && (
    <button
      type="button"
      className="exp-category-arrow exp-category-arrow-right"
      onClick={() => {
        const el = tagsScrollRef.current;

  if (el) {
    el.scrollTo({
      left: el.scrollWidth + 30,
      behavior: "smooth",
    });
  }
      }}
    >
      <ChevronRightIcon />
    </button>
  )}

</div>

             
          </div>

          <div className="exp-results-count">
            {!loading && totalCount > 0 ? `${totalCount.toLocaleString()}+ results` : ""}

             <select
              className="exp-sort-select"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value);  }}
            >
              <option value="DEFAULT">Sort: Featured</option>
              <option value="PRICE_LOW">Price: Low to high</option>
              <option value="PRICE_HIGH">Price: High to low</option>
            </select>
          </div>

          <FilterModal
    open={isFilterOpen} 
    onClose={handleCloseFilter} 
            onSubmit={handleApplyFilters}
            price={price}
            flags={specials}
            currency={currencyType}
            durations={durations}

          />
          
          <Dialog
    open={isMoreModalOpen}
    onClose={() => setIsMoreModalOpen(false)}
    fullWidth
    maxWidth="sm"
    scroll="paper"
    PaperProps={{
      sx: { borderRadius: 3, maxHeight: '80vh' }
    }}
  >
    <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontWeight: 'bold' }}>All Tags</span>
      <IconButton
                aria-label="close"
                style={{width:'fit-content'}}
        onClick={() => setIsMoreModalOpen(false)}
        sx={{
          color: (theme) => theme.palette.grey[500],
          bgcolor: (theme) => theme.palette.grey[100],
          '&:hover': { bgcolor: (theme) => theme.palette.grey[200] }
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>

    <DialogContent dividers sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {tags?.map((tag) => {
          const isActive = activeTag.includes(tag.tagId);

          return (
            <button
              key={`modal-${tag.tagId}`}
              type="button"
              className={`exp-category-pill ${
                isActive ? "exp-category-pill-active" : ""
              }`}
              onClick={() => {
                setActiveTag((prev) =>
                  prev.includes(tag.tagId)
                    ? prev.filter((id) => id !== tag.tagId)
                    : [...prev, tag.tagId]
                );
              }}
            >
              {tag.name}
              {isActive && (
                <CloseIcon
                  className="exp-category-close"
                  fontSize="small"
                />
              )}
            </button>
          );
        })}
      </Box>
    </DialogContent>

    <DialogActions sx={{ p: 2 }}>
      <Button
        onClick={() => setIsMoreModalOpen(false)}
        variant="contained"
        sx={{
          bgcolor: '#008768',
          color: 'white',
          fontWeight: 'bold',
          textTransform: 'none',
          borderRadius: 2,
          '&:hover': { bgcolor: '#006B52' }
        }}
      >
        Done
      </Button>
    </DialogActions>
  </Dialog>

          <div className="exp-product-grid">
            {loading && (
              <div className="exp-loading-grid">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div className="exp-skeleton-card" key={i}>
                    <div className="exp-skeleton-img" />
                    <div className="exp-skeleton-line" style={{ width: "80%" }} />
                    <div className="exp-skeleton-line" style={{ width: "50%" }} />
                  </div>
                ))}
              </div>
            )}

            {!loading && products?.length === 0 && (
              <div className="exp-empty-state">
                <h3>No experiences match your filters</h3>
                <p>{errorMsg || "Try widening your price range or clearing a filter."}</p>
              </div>
            )}

            {!loading &&
              products?.map((product) => (
                <ExperienceProductCard
                  key={product.productCode}
                  product={product}
                  onSelect={handleSelectProduct}
                />
              ))}
          </div>

          {!loading && totalPages > 1 && (
            <nav className="exp-pagination" aria-label="Results pagination">
              <button
                type="button"
                className="exp-pagination-arrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>

              {buildPageList(currentPage, totalPages).map((item, idx) =>
                item === "..." ? (
                  <span className="exp-pagination-ellipsis" key={`ellipsis-${idx}`}>
                    ...
                  </span>
                ) : (
                  <button
                    type="button"
                    key={item}
                    className={`exp-pagination-number${
                      item === currentPage ? " exp-pagination-number-active" : ""
                    }`}
                    onClick={() => handlePageChange(item)}
                    aria-current={item === currentPage ? "page" : undefined}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                className="exp-pagination-arrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </nav>
          )}
        </div>
      </div>

      <FooterNew />
    </div>
  );
}
